import PropTypes from 'prop-types';
import React, { Children, memo, useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { DraggableCore } from 'react-draggable';

import style from './style.module.css';

const DEFAULT_WIDTH_PERCENT = (5 / 12) * 100;
const MIN_WIDTH_PERCENT = 10;
const MAX_WIDTH_PERCENT = 90;
const MIN_COLUMN_WIDTH = 300;
const HANDLE_WIDTH = 12;

const readStoredWidth = (storageKey, defaultWidth) => {
  try {
    const storedWidth = Number(window.localStorage.getItem(storageKey));

    return storedWidth >= MIN_WIDTH_PERCENT && storedWidth <= MAX_WIDTH_PERCENT ? storedWidth : defaultWidth;
  } catch (error) {
    return defaultWidth;
  }
};

const storeWidth = (storageKey, width) => {
  try {
    window.localStorage.setItem(storageKey, String(width));
  } catch (error) {
    // Storage may be unavailable (private mode); the width just won't persist.
  }
};

const toClampedPercent = (widthPx, hostWidth, minColumnWidth) => {
  const maxWidthPx = Math.max(minColumnWidth, hostWidth - HANDLE_WIDTH - minColumnWidth);
  const clampedWidthPx = Math.min(Math.max(widthPx, minColumnWidth), maxWidthPx);

  return Math.min(Math.max((clampedWidthPx / hostWidth) * 100, MIN_WIDTH_PERCENT), MAX_WIDTH_PERCENT);
};

const ResizableColumnsComponent = ({
  storageKey,
  defaultWidth = DEFAULT_WIDTH_PERCENT,
  minColumnWidth = MIN_COLUMN_WIDTH,
  className,
  children,
}) => {
  const hostNodeRef = useRef(null);
  const handleNodeRef = useRef(null);
  const pointerOffsetRef = useRef(0);
  const resizeFrameRef = useRef(null);
  const [width, setWidth] = useState(() => readStoredWidth(storageKey, defaultWidth));
  const [firstColumn, ...restColumns] = Children.toArray(children);

  useEffect(() => () => cancelAnimationFrame(resizeFrameRef.current), []);

  const getWidthPercent = useCallback(
    x => {
      const { paddingLeft, paddingRight } = window.getComputedStyle(hostNodeRef.current);
      const hostWidth = hostNodeRef.current.clientWidth - parseFloat(paddingLeft) - parseFloat(paddingRight);

      return toClampedPercent(x + pointerOffsetRef.current, hostWidth, minColumnWidth);
    },
    [minColumnWidth],
  );

  const handleDragStart = useCallback((event, { x }) => {
    pointerOffsetRef.current = hostNodeRef.current.firstElementChild.offsetWidth - x;
  }, []);

  const handleDrag = useCallback(
    (event, { x }) => {
      setWidth(getWidthPercent(x));

      // Grids recalculate their size on window resize only.
      cancelAnimationFrame(resizeFrameRef.current);
      resizeFrameRef.current = requestAnimationFrame(() => {
        window.dispatchEvent(new Event('resize'));
      });
    },
    [getWidthPercent],
  );

  const handleDragStop = useCallback(
    (event, { x }) => {
      storeWidth(storageKey, getWidthPercent(x));
    },
    [storageKey, getWidthPercent],
  );

  return (
    <div
      ref={hostNodeRef}
      className={classNames(style.host, className)}
      style={{ '--first-column-width': `${width}%` }}
    >
      {firstColumn}
      {restColumns.length > 0 && (
        <DraggableCore nodeRef={handleNodeRef} onDrag={handleDrag} onStart={handleDragStart} onStop={handleDragStop}>
          <span ref={handleNodeRef} className={style.handle} />
        </DraggableCore>
      )}
      {restColumns}
    </div>
  );
};

ResizableColumnsComponent.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  defaultWidth: PropTypes.number,
  minColumnWidth: PropTypes.number,
  storageKey: PropTypes.string.isRequired,
};

/** Row of columns where the first column width is changed by dragging the handle after it and kept in localStorage. */
export const ResizableColumns = memo(ResizableColumnsComponent);
